import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  connectCurrentRefinements,
  connectRefinementList,
  connectRange,
  connectSearchBox
} from 'react-instantsearch-dom';
import FilterTag from '@components/atoms/filter-tag/filterTag';
import Headline from '@components/atoms/headline/headline';
import CloseIcon from '@components/icons/close.svg';
import MagnifyingGlas from '@components/icons/futurium-iconset/magnifying-glas.svg';

import 'rheostat/initialize';
import Rheostat from 'rheostat';
import StyledEducationRefinementReset from './educationRefinementReset.style';
import StyledEducationRefinementSearch from './educationRefinementSearch.style';
import StyledEducationRefinementSlider from './educationRefinementSlider.style';
import StyledEducationRefinement from './educationRefinement.style';
import 'rheostat/css/rheostat.css';
import { EducationLibraryFilterContext } from '../../../organisms/education/library-filter/educationLibraryFilter';

const TrueFalseRefinement = ({ refine, headline }) => {
  const [selected, setSelected] = useState();
  const { isFilterReset, setIsFilterReset } = useContext(EducationLibraryFilterContext);

  useEffect(() => {
    if (isFilterReset) {
      setSelected();
    }
  }, [isFilterReset]);

  return (
    <StyledEducationRefinement>
      {headline && (
        <Headline className="refinement__headline" tag="p">
          {headline}
        </Headline>
      )}

      <div className="refinement__grid">
        <button
          type="button"
          onClick={event => {
            event.preventDefault();
            refine(['true']);
            setSelected(true);
            setIsFilterReset(false);
          }}
        >
          <FilterTag small border inverted={selected}>
            Ja
          </FilterTag>
        </button>

        <button
          type="button"
          onClick={event => {
            event.preventDefault();
            refine(['false']);
            setSelected(false);
            setIsFilterReset(false);
          }}
        >
          <FilterTag small border inverted={selected === false}>
            Nein
          </FilterTag>
        </button>
      </div>
    </StyledEducationRefinement>
  );
};

TrueFalseRefinement.propTypes = {
  refine: PropTypes.func.isRequired,
  headline: PropTypes.string
};

export const CustomTrueFalseRefinement = connectRefinementList(TrueFalseRefinement);

const BasicRefinement = ({ items, refine, headline, ...props }) => {
  if (items.length === 0) {
    return null;
  }
  return (
    <StyledEducationRefinement {...props}>
      {headline && (
        <Headline className="refinement__headline" tag="p">
          {headline}
        </Headline>
      )}
      <div className="refinement__grid">
        {items.map(item => (
          <button
            type="button"
            key={item.label}
            onClick={event => {
              event.preventDefault();
              refine(item.value);
            }}
          >
            <FilterTag small border inverted={!!item.isRefined}>
              {item.label}
            </FilterTag>
          </button>
        ))}
      </div>
    </StyledEducationRefinement>
  );
};

BasicRefinement.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      isRefined: PropTypes.bool.isRequired
    })
  ).isRequired,
  refine: PropTypes.func.isRequired,
  headline: PropTypes.string
};

export const CustomBasicRefinement = connectRefinementList(BasicRefinement);

const TopicRefinement = ({ items, refine, headline, ...props }) => {
  if (items.length === 0) {
    return null;
  }

  const sortArray = [
    'ressources',
    'city',
    'nutrition',
    'health',
    'mobility',
    'energy',
    'work',
    'democracy'
  ];
  items.sort((a, b) => sortArray.indexOf(a.label) - sortArray.indexOf(b.label));

  return (
    <StyledEducationRefinement {...props}>
      {headline && (
        <Headline className="refinement__headline" tag="p">
          {headline}
        </Headline>
      )}
      <div className="refinement__grid">
        {items.map(({ label: itemLabel, value, isRefined }) => {
          let label;
          switch (itemLabel) {
            case 'ressources':
              label = 'Rohstoffe';
              break;
            case 'city':
              label = 'Städte';
              break;
            case 'mobility':
              label = 'Mobilität';
              break;
            case 'work':
              label = 'Arbeit';
              break;
            case 'health':
              label = 'Gesundheit';
              break;
            case 'energy':
              label = 'Energie';
              break;
            case 'nutrition':
              label = 'Ernährung';
              break;
            case 'agriculture':
              label = 'Landwirtschaft';
              break;
            case 'democracy':
              label = 'Demokratie';
              break;
            default:
              label = itemLabel;
          }

          return (
            <button
              type="button"
              key={itemLabel}
              onClick={event => {
                event.preventDefault();
                refine(value);
              }}
            >
              <FilterTag small border inverted={!!isRefined}>
                {label}
              </FilterTag>
            </button>
          );
        })}
      </div>
    </StyledEducationRefinement>
  );
};

TopicRefinement.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      isRefined: PropTypes.bool.isRequired
    })
  ).isRequired,
  refine: PropTypes.func.isRequired,
  headline: PropTypes.string
};

export const CustomTopicRefinement = connectRefinementList(TopicRefinement);

const RangeSliderRefinement = ({
  min: propMin,
  max: propMax,
  currentRefinement,
  canRefine,
  refine,
  headline
}) => {
  const [min, setMin] = React.useState(propMin);
  const [max, setMax] = React.useState(propMax);

  React.useEffect(() => {
    if (canRefine) {
      setMin(currentRefinement.min);
      setMax(currentRefinement.max);
    }
  }, [currentRefinement.min, currentRefinement.max]);

  if (propMin === propMax) {
    return null;
  }

  const onChange = ({ values: [newMin, newMax] }) => {
    if (currentRefinement.min !== newMin || currentRefinement.max !== newMax) {
      refine({ min: newMin, max: newMax });
    }
  };

  const onValuesUpdated = ({ values: [newMin, newMax] }) => {
    setMin(newMin);
    setMax(newMax);
  };

  return (
    <StyledEducationRefinement>
      {headline && (
        <Headline className="refinement__headline" tag="p">
          {headline}
        </Headline>
      )}
      <StyledEducationRefinementSlider>
        <Rheostat
          min={propMin}
          max={propMax}
          values={[currentRefinement.min, currentRefinement.max]}
          onChange={onChange}
          onValuesUpdated={onValuesUpdated}
        >
          <div className="refinement__range-state">
            <div className="refinement__range-value">{min} Min</div>
            <div className="refinement__range-value">{max} Min</div>
          </div>
        </Rheostat>
      </StyledEducationRefinementSlider>
    </StyledEducationRefinement>
  );
};

RangeSliderRefinement.propTypes = {
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  currentRefinement: PropTypes.shape({
    min: PropTypes.number.isRequired,
    max: PropTypes.number.isRequired
  }).isRequired,
  canRefine: PropTypes.bool.isRequired,
  refine: PropTypes.func.isRequired,
  headline: PropTypes.string
};

export const CustomRangeSliderRefinement = connectRange(RangeSliderRefinement);

const SearchBoxRefinement = ({ currentRefinement, isSearchStalled, refine, headline }) => (
  <StyledEducationRefinement>
    {headline && (
      <Headline className="refinement__headline" tag="p">
        {headline}
      </Headline>
    )}
    <StyledEducationRefinementSearch>
      <form noValidate action="" role="search">
        <div className="search__input-wrapper">
          <MagnifyingGlas className="search__input-icon" width="30" height="30" />
          <input
            type="search"
            placeholder="Suchbegriff"
            value={currentRefinement}
            onChange={event => refine(event.currentTarget.value)}
          />
          {currentRefinement && (
            <button className="search__input-reset" type="button" onClick={() => refine('')}>
              <CloseIcon width="14" height="14" />
            </button>
          )}
        </div>
        {isSearchStalled ? 'My search is stalled' : ''}
      </form>
    </StyledEducationRefinementSearch>
  </StyledEducationRefinement>
);

SearchBoxRefinement.propTypes = {
  currentRefinement: PropTypes.string.isRequired,
  isSearchStalled: PropTypes.bool.isRequired,
  refine: PropTypes.func.isRequired,
  headline: PropTypes.string
};

export const CustomSearchRefinement = connectSearchBox(SearchBoxRefinement);

const ClearRefinements = ({ items, refine, title = 'Zurücksetzen' }) => {
  const filteredItems = items.filter(item => item.attribute !== 'learningType');
  const { setIsFilterReset } = useContext(EducationLibraryFilterContext);

  return (
    <StyledEducationRefinementReset>
      <button
        type="button"
        onClick={() => {
          refine(filteredItems);
          setIsFilterReset(true);
        }}
        disabled={!items.length}
      >
        {title}
      </button>
    </StyledEducationRefinementReset>
  );
};

ClearRefinements.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape).isRequired,
  refine: PropTypes.func.isRequired,
  title: PropTypes.string
};

export const CustomClearRefinements = connectCurrentRefinements(ClearRefinements);
